
import React, { useState, useEffect } from 'react';
import { AgentProfile } from '../types';
import { MapPin, LogOut, UserPlus, Check, Share2, Sparkles, Building2, ChevronDown, ChevronUp, BarChart3, Users, MessageSquare, PhoneCall, X, ExternalLink, Globe, Loader2 } from 'lucide-react';
import { ToastType } from './Toast';
import { supabase } from '../supabase';

interface ProfileHeaderProps {
  agent: AgentProfile;
  isOwner: boolean;
  onAddProperty: () => void;
  onEditProfile: () => void;
  onLogout: () => void;
  onUpgrade: () => void;
  listingCount: number;
  onToast?: (message: string, type: ToastType) => void;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ agent, isOwner, onAddProperty, onEditProfile, onLogout, onUpgrade, listingCount, onToast }) => {
  const [copied, setCopied] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [stats, setStats] = useState({
    profile_view: 0,
    whatsapp_lead: 0,
    phone_call: 0,
    share: 0
  });

  const fetchInsights = async () => {
    if (!supabase || !agent.id) return;
    setIsLoadingInsights(true);
    try {
      // Fetch counts for each action type in parallel
      const types = ['profile_view', 'whatsapp_lead', 'phone_call', 'share'];
      const results = await Promise.all(
        types.map(type => 
          supabase!
            .from('agent_stats')
            .select('*', { count: 'exact', head: true })
            .eq('agent_id', agent.id)
            .eq('action_type', type)
        )
      );

      const newStats = {
        profile_view: results[0].count || 0,
        whatsapp_lead: results[1].count || 0,
        phone_call: results[2].count || 0,
        share: results[3].count || 0
      };

      setStats(newStats);
    } catch (err) {
      console.error("Error fetching insights:", err);
      if (onToast) onToast("Failed to load business insights.", "error");
    } finally {
      setIsLoadingInsights(false);
    }
  };

  useEffect(() => {
    if (showInsights) {
      fetchInsights();
    }
  }, [showInsights, agent.id]);

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        if (onToast) onToast("Profile link copied to clipboard!", "success");
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error("No clipboard access");
      }
    } catch (err) {
      console.warn("Clipboard failed", err);
      if (onToast) onToast("Couldn't copy link automatically.", "error");
    }
  };

  const handleShareProfile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrl = `https://nas-ng.vercel.app/?agent=${agent.id}`;
    const displayName = agent.full_name || agent.business_name || agent.name;
    const shareText = `Check out ${displayName}'s professional property catalog on Findily.`;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
      navigator.share({
        title: displayName,
        text: shareText,
        url: shareUrl,
      }).catch((err) => {
        if (err.name !== 'AbortError') {
          copyToClipboard(shareUrl);
        }
      });
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const getPlanGradient = (plan: string) => {
    switch (plan) {
      case 'Elite':
        return 'bg-gradient-to-tr from-amber-400 via-yellow-500 to-orange-600';
      case 'Pro':
        return 'bg-gradient-to-tr from-blue-400 via-indigo-500 to-blue-600';
      default:
        return 'bg-gradient-to-tr from-gray-300 via-gray-400 to-gray-500';
    }
  };

  const BIO_LIMIT = 120;
  const shouldShowBioMore = agent.bio && agent.bio.length > BIO_LIMIT;
  const displayedBio = (shouldShowBioMore && !isBioExpanded) 
    ? `${agent.bio.substring(0, BIO_LIMIT)}...` 
    : agent.bio;

  const insightsConfig = [
    { label: "Profile Views", value: stats.profile_view, icon: Users, color: "text-blue-500", bg: "bg-blue-500/10" },
    { label: "WhatsApp Leads", value: stats.whatsapp_lead, icon: MessageSquare, color: "text-green-500", bg: "bg-green-500/10" },
    { label: "Phone Calls", value: stats.phone_call, icon: PhoneCall, color: "text-indigo-500", bg: "bg-indigo-500/10" },
    { label: "Total Shares", value: stats.share, icon: Share2, color: "text-pink-500", bg: "bg-pink-500/10" }
  ];

  return (
    <div className="px-4 pt-6 pb-4 md:pt-10 md:pb-8">
      {/* Profile Info Section */}
      <div className="flex items-center gap-6 md:gap-16 mb-6">
        <div className="relative">
          <div className={`w-20 h-20 md:w-40 md:h-40 rounded-full p-[3px] transition-all duration-500 ${getPlanGradient(agent.plan)}`}>
            <div className="w-full h-full rounded-full p-[2px] bg-white dark:bg-black">
              <img src={agent.avatar} alt={agent.name} className="w-full h-full rounded-full object-cover shadow-inner" />
            </div>
          </div>
          <div className={`absolute -bottom-1 -right-1 px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-tighter text-white shadow-lg ${getPlanGradient(agent.plan)}`}>
            {agent.plan}
          </div>
        </div>

        <div className="flex-1 flex justify-around md:justify-start md:gap-20 items-center">
          {/* Posts Count */}
          <div className="flex flex-col items-center">
            <span className="text-sm md:text-xl font-black text-gray-900 dark:text-white leading-tight">{listingCount}</span>
            <span className="text-[10px] md:text-sm font-medium text-gray-500 dark:text-zinc-500 uppercase tracking-widest">posts</span>
          </div>

          {/* Insights (Owner Only) */}
          {isOwner && (
            <button 
              onClick={() => setShowInsights(true)}
              className="flex flex-col items-center group active:scale-95 transition-all"
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <BarChart3 className="w-4 h-4 md:w-5 md:h-5 text-gray-900 dark:text-white group-hover:text-blue-500 transition-colors" />
              </div>
              <span className="text-[10px] md:text-sm font-medium uppercase tracking-widest text-gray-500 dark:text-zinc-500 group-hover:text-blue-500 transition-colors">
                Insights
              </span>
            </button>
          )}
          
          {/* Share Button */}
          <button 
            onClick={handleShareProfile}
            className="flex flex-col items-center group active:scale-95 transition-all"
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              {copied ? (
                <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
              ) : (
                <Share2 className="w-4 h-4 md:w-5 md:h-5 text-gray-900 dark:text-white group-hover:text-blue-500 transition-colors" />
              )}
            </div>
            <span className={`text-[10px] md:text-sm font-medium uppercase tracking-widest transition-colors ${copied ? 'text-green-500' : 'text-gray-500 dark:text-zinc-500 group-hover:text-blue-500'}`}>
              {copied ? 'Copied' : 'Share Profile'}
            </span>
          </button>
        </div>
      </div>

      {/* Profile Text Content */}
      <div className="mb-6 space-y-1">
        <div className="flex items-center gap-2 mb-0.5">
          <h1 className="text-sm md:text-lg font-black text-gray-900 dark:text-white tracking-tight">
            {agent.full_name || agent.name}
          </h1>
          {agent.verification_status === 'verified' && <VerifiedBadge className="w-4 h-4 text-blue-500" />}
          {agent.is_pioneer && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-md">
              <Sparkles className="w-3 h-3 fill-current" />
              <span className="text-[8px] font-black uppercase tracking-widest">Pioneer Agent</span>
            </div>
          )}
        </div>
        
        <div className="text-xs md:text-sm text-gray-400 dark:text-zinc-500 font-bold">
          Real Estate Professional • {agent.business_name}
        </div>

        <div className="max-w-lg">
          <p className="text-xs md:text-sm text-gray-800 dark:text-zinc-300 leading-snug whitespace-pre-wrap">
            {displayedBio}
            {shouldShowBioMore && (
              <button 
                onClick={() => setIsBioExpanded(!isBioExpanded)}
                className="ml-1 text-blue-500 font-black hover:underline focus:outline-none"
              >
                {isBioExpanded ? 'less' : 'more'}
              </button>
            )}
          </p>
        </div>

        <div className="space-y-1.5 mt-2">
          <div className="flex items-center gap-1 text-[11px] md:text-xs font-bold text-gray-400">
            <MapPin className="w-3 h-3" /> {agent.location}
          </div>

          {agent.office_address && (
            <div className="flex flex-col">
              <div className="flex items-start gap-1.5 text-blue-500 dark:text-blue-400 font-bold group">
                <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <p className={`text-[11px] md:text-xs leading-tight transition-all ${isAddressExpanded ? '' : 'line-clamp-1'}`}>
                  {agent.office_address}
                </p>
                {agent.office_address.length > 50 && (
                  <button 
                    onClick={() => setIsAddressExpanded(!isAddressExpanded)}
                    className="p-0.5 bg-gray-100 dark:bg-zinc-800 rounded-md active:scale-90 transition-transform"
                  >
                    {isAddressExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Primary Action Buttons */}
      <div className="flex gap-2">
        {isOwner ? (
          <>
            <button onClick={onEditProfile} className="flex-1 py-1.5 md:py-2 bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-white text-xs font-black rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-700 transition-all active:scale-95">Edit Profile</button>
            <button onClick={onAddProperty} className="flex-1 py-1.5 md:py-2 bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-white text-xs font-black rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-700 transition-all active:scale-95">Add Property</button>
            <button onClick={onLogout} className="p-1.5 md:p-2 bg-gray-100 dark:bg-zinc-800 text-gray-400 dark:text-zinc-500 rounded-lg transition-all active:scale-95"><LogOut className="w-4 h-4" /></button>
          </>
        ) : (
          <>
            <button onClick={() => window.open(`https://wa.me/${agent.whatsapp}`, '_blank')} className="flex-[4] py-1.5 md:py-2 bg-blue-500 text-white text-xs font-black rounded-lg hover:bg-blue-600 transition-all active:scale-95">WhatsApp</button>
            <button onClick={() => window.location.href = `tel:${agent.phone}`} className="flex-[4] py-1.5 md:py-2 bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-white text-xs font-black rounded-lg transition-all active:scale-95">Call Now</button>
            <button className="flex-1 p-1.5 md:p-2 bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-white rounded-lg flex items-center justify-center transition-all active:scale-95"><UserPlus className="w-4 h-4" /></button>
          </>
        )}
      </div>

      {/* Insights Modal */}
      {showInsights && (
        <div className="fixed inset-0 z-[200] flex items-end md:items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setShowInsights(false)}>
          <div 
            className="bg-white dark:bg-zinc-950 w-full max-w-md rounded-t-[32px] md:rounded-[32px] shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-500"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 dark:border-zinc-900 flex justify-between items-center bg-white/50 dark:bg-zinc-950/50 backdrop-blur-md sticky top-0">
               <div>
                  <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight uppercase">Business Insights</h2>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Performance Metrics</p>
               </div>
               <button onClick={() => setShowInsights(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-900 rounded-full transition-colors"><X className="w-6 h-6 text-gray-400" /></button>
            </div>
            
            <div className="p-8 space-y-6">
              {isLoadingInsights ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Gathering Data...</span>
                </div>
              ) : (
                <>
                  {/* Stats List (Minimal Redesign) */}
                  <div className="flex flex-col divide-y divide-gray-100 dark:divide-zinc-900">
                    {insightsConfig.map((stat, i) => (
                      <div key={i} className="flex items-center justify-between py-5 first:pt-0 last:pb-0">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center ${stat.color}`}>
                            <stat.icon className="w-5 h-5" />
                          </div>
                          <span className="text-xs font-black text-gray-500 dark:text-zinc-400 uppercase tracking-widest">
                            {stat.label}
                          </span>
                        </div>
                        <span className="text-2xl font-black text-gray-900 dark:text-white tracking-tighter">
                          {stat.value.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Minimal Reach Message */}
                  <div className="pt-6 border-t border-gray-100 dark:divide-zinc-900">
                    <div className="p-5 bg-gray-50 dark:bg-zinc-900 rounded-2xl flex items-center gap-4">
                      <Globe className="w-5 h-5 text-blue-500" />
                      <p className="text-[11px] font-bold text-gray-600 dark:text-zinc-400 uppercase tracking-widest leading-relaxed">
                        Your properties are visible to a <span className="text-blue-500">global audience</span>. Data updates every few hours.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 dark:border-zinc-900 bg-gray-50 dark:bg-zinc-900/50 flex justify-center">
               <button 
                onClick={() => setShowInsights(false)}
                className="w-full py-4 bg-black dark:bg-white text-white dark:text-black rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] active:scale-[0.98] transition-all"
               >
                 Close Dashboard
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VerifiedBadge = ({ className }: { className?: string }) => (
  <div className={`relative overflow-hidden rounded-full ${className} flex items-center justify-center`}>
     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/70 to-transparent -translate-x-full animate-shimmer" />
     <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
     </svg>
  </div>
);
